import { IconChevronRight } from '@tvg/fuse-icons';
import PropTypes from 'prop-types';
import React from 'react';
import { SpanButton } from '../../fuse-buttons/src/buttons';
import {
	StyledContentBanner,
	StyledHeading,
	StyledHeadingSpan,
	StyledParagraph,
	StyledSubHeading,
	StyledText,
	StyledTextWrapper,
} from './styled-content-banner-2';

const defaultProps = {
	className: undefined,
	dataTestId: undefined,
	dataAnalyticsId: undefined,
	link: {
		href: undefined,
		onClick: () => {},
		text: undefined,
	},
	textContent: {
		enhancedText: undefined,
		heading: undefined,
		headingTag: 'h2',
		legalText: undefined,
		paragraph: undefined,
		subHeading: undefined,
		subHeadingTag: 'h3',
	},
	variant: 'brand',
};

const propTypes = {
	className: PropTypes.string,
	dataTestId: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	link: PropTypes.shape({
		href: PropTypes.string,
		onClick: PropTypes.func,
		text: PropTypes.string,
	}),
	textContent: PropTypes.shape({
		enhancedText: PropTypes.string,
		heading: PropTypes.string,
		headingTag: PropTypes.string,
		legalText: PropTypes.string,
		paragraph: PropTypes.string,
		subHeading: PropTypes.string,
		subHeadingTag: PropTypes.string,
	}),
	variant: PropTypes.oneOf(['brand', 'black', 'blackWithBorder']),
};

const enhanceText = (heading, enhancedText) =>
	heading
		.split(' ')
		.map((word) =>
			word === enhancedText
				? `<span class="fuse-content-banner-2__enhanced-text" data-testid="fuse-content-banner-2-enhanced-text-test-id">&nbsp;${word}&nbsp;</span>`
				: word,
		)
		.join(' ');

function ContentBanner2({
	className,
	variant,
	textContent,
	link,
	dataTestId,
	dataAnalyticsId,
	...props
}) {
	const { headingTag, heading, enhancedText, subHeading, subHeadingTag, paragraph, legalText } =
		textContent;

	const isValidCTA = !!link.href || !!link.onClick;

	return (
		<StyledContentBanner
			variant={variant}
			className={className}
			data-testid={dataTestId || 'fuse-content-banner-2-test-id'}
			data-analyticsid={dataAnalyticsId}
			as={isValidCTA && (link.href ? 'a' : 'button')}
			href={link.href}
			onClick={!link.href ? link.onClick : undefined}
			{...props}
		>
			<StyledText variant={variant}>
				{heading && (
					<StyledHeading size={{ xxs: '7', m: '8', l: '10' }} tag={headingTag} type="display">
						{enhancedText ? (
							<StyledHeadingSpan
								variant={variant}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: enhancedText ? enhanceText(heading, enhancedText) : heading,
								}}
							/>
						) : (
							heading
						)}
					</StyledHeading>
				)}
				<StyledTextWrapper>
					{subHeading && (
						<StyledSubHeading
							size={{ xxs: '3', l: '4' }}
							tag={subHeadingTag}
							type="heading"
							variant={variant}
						>
							{subHeading}
						</StyledSubHeading>
					)}
					{paragraph && (
						<StyledParagraph
							size="1"
							tag="p"
							type="paragraph"
							variant={variant}
							data-testid="fuse-content-banner-2-paragraph-test-id"
							className="fuse-content-banner-2__paragraph"
						>
							{paragraph}
						</StyledParagraph>
					)}
					{legalText && (
						<StyledParagraph
							size="m2"
							tag="p"
							type="paragraph"
							variant={variant}
							data-testid="fuse-content-banner-2-legal-text-test-id"
							className="fuse-content-banner-2__legal-text"
						>
							{legalText}
						</StyledParagraph>
					)}
				</StyledTextWrapper>
			</StyledText>
			{(link.href || link.onClick) && link.text && (
				<SpanButton
					icon={<IconChevronRight />}
					iconRight
					variant="tertiary"
					data-testid="fuse-content-banner-2-span-button-test-id"
					className="fuse-content-banner-2__span-button fuse-span-button"
				>
					{link.text}
				</SpanButton>
			)}
		</StyledContentBanner>
	);
}

ContentBanner2.propTypes = propTypes;
ContentBanner2.defaultProps = defaultProps;
ContentBanner2.displayName = 'ContentBanner2';

export default ContentBanner2;
